import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import authProvider from './authProvider';
import dataProvider from './dataProvider';
import AuthCallback from './AuthCallback';
import LoginPage from './LoginPage';
import CustomLayout from './Layout';
import PageTitle from './PageTitle';
import { theme } from './theme';
import { i18nProvider } from './i18nProvider';
import { UserList, UserEdit, UserCreate } from './resources/users';
import { RoleList, RoleEdit, RoleCreate } from './resources/roles';
import { PermissionList, PermissionEdit, PermissionCreate } from './resources/permissions';
import { ProductList, ProductEdit, ProductCreate } from './resources/products';
import { LicenseList, LicenseEdit, LicenseCreate } from './resources/licenses';
import { CompanyList, CompanyEdit, CompanyCreate } from './resources/companies';
import { canManagePlatformTenants } from './resources/companyPermissions';
import RegisterPage from './RegisterPage';
import TranslateHelper from './TranslateHelper';
import Dashboard from './Dashboard';
import OrganizationSettings from './components/Settings/OrganizationSettings';
import CompanyOnboardingWizard from './components/CompanyOnboardingWizard';
import CompanyDocuments from './components/CompanyDocuments';
import GlobalDocuments from './components/GlobalDocuments';

// Initialize auto-login flag BEFORE React Admin starts
// Always set auto_login so checkAuth can redirect to SSO immediately
// (avoids the slow checkAuth → logout → LoginPage → login cycle)
if (window.location.pathname !== '/auth-callback') {
    localStorage.setItem('auto_login_in_progress', 'true');
    localStorage.removeItem('user_logged_out');
} else {
    localStorage.removeItem('auto_login_in_progress');
}

// Der Admin ist produktuebergreifend (online:academy + azubi:web) und traegt
// seit 2026-09 die admin:panel-Zwischenmarke der generation:l-Familie — kein
// Mandanten-Branding mehr (das lebt in der App).
const AppContent = () => {
    return (
        <Admin
            dataProvider={dataProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            loginPage={LoginPage}
            layout={CustomLayout}
            theme={theme}
            title="admin:panel"
            dashboard={Dashboard}
            requireAuth
        >
            {(permissions) => {
                const isOperator = canManagePlatformTenants(permissions);
                return (
                    <>
                        <PageTitle />
                        <Resource name="companies" list={CompanyList} edit={CompanyEdit} create={isOperator ? CompanyCreate : undefined} icon={BusinessIcon} />
                        <Resource name="users" list={UserList} edit={UserEdit} create={UserCreate} icon={PeopleIcon} />
                        {/* Einladungen sind 2026-09 in die App gewandert (azubi:web-Feedback:
                            jede App haendelt ihre Einladungen selbst). Die Redemption-Route
                            /register unten bleibt uebergangsweise, damit bereits versandte
                            Einladungs-Links (ADMIN_URL-Basis) gueltig bleiben. */}
                        {/* Operator-only Resources — für COMPANY_ADMIN etc. ist der Endpoint 403,
                            also gar nicht erst in die Nav aufnehmen statt Dead-End zu zeigen. */}
                        {isOperator && <Resource name="roles" list={RoleList} edit={RoleEdit} create={RoleCreate} icon={SecurityIcon} />}
                        {isOperator && <Resource name="permissions" list={PermissionList} edit={PermissionEdit} create={PermissionCreate} icon={LockIcon} />}
                        {isOperator && <Resource name="products" list={ProductList} edit={ProductEdit} create={ProductCreate} icon={ShoppingCartIcon} />}
                        <Resource name="licenses" list={LicenseList} edit={LicenseEdit} create={LicenseCreate} icon={CardMembershipIcon} />
                        {/* Bare Resource für Referenzen aus Lizenzen/Produkten */}
                        <Resource name="courses" />
                        <CustomRoutes noLayout>
                            <Route path="/register" element={<RegisterPage />} />
                        </CustomRoutes>
                        <CustomRoutes>
                            {/* Profil, Passwort-Aenderung und Branding leben seit 2026-09
                                ausschliesslich in der App (azubi:web-Feedback). */}
                            <Route path="/translate" element={<TranslateHelper />} />
                            <Route path="/organization-settings" element={<OrganizationSettings />} />
                            {isOperator && <Route path="/company-onboarding" element={<CompanyOnboardingWizard />} />}
                            <Route path="/documents" element={<CompanyDocuments />} />
                            {isOperator && <Route path="/legal-documents" element={<GlobalDocuments />} />}
                        </CustomRoutes>
                    </>
                );
            }}
        </Admin>
    );
};

const App = () => {
    console.log('[App] Rendering, pathname:', window.location.pathname);

    // Handle auth callback outside of React Admin to avoid auth checks
    if (window.location.pathname === '/auth-callback') {
        console.log('[App] Rendering AuthCallback');
        return <AuthCallback />;
    }

    console.log('[App] Rendering Admin');

    return <AppContent />;
};

export default App;
